import { useState, useRef, useEffect } from "react";
import {
  Save,
  X,
  Loader2,
  Pipette,
  Undo2,
  Redo2,
  FlipHorizontal,
  FlipVertical,
  RotateCcw,
  Sun,
  Moon,
  ArrowRight,
  Eraser,
  Crop as CropIcon,
  ChevronDown,
  Trash2,
  Palette,
} from "lucide-react";

// ======== Color Helpers ========

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("");
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

// ======== Props ========

interface DesignEditorPanelProps {
  designUrl: string | null;
  onClose: () => void;
  /** Called with edited data URL when user clicks save */
  onSave: (dataUrl: string) => void;
  /** Optional: show mockup-specific buttons (light/dark) */
  showMockupActions?: boolean;
  onSaveLight?: (dataUrl: string) => void;
  onSaveDark?: (dataUrl: string) => void;
  onClearLight?: () => void;
  onClearDark?: () => void;
  onApplyToCanvas?: (dataUrl: string) => void;
  lightDesignUrl?: string | null;
  darkDesignUrl?: string | null;
}

export default function DesignEditorPanel({
  designUrl,
  onClose,
  onSave,
  showMockupActions = false,
  onSaveLight,
  onSaveDark,
  onClearLight,
  onClearDark,
  onApplyToCanvas,
  lightDesignUrl,
  darkDesignUrl,
}: DesignEditorPanelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const [originalImageData, setOriginalImageData] = useState<ImageData | null>(null);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [history, setHistory] = useState<ImageData[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [pickingColor, setPickingColor] = useState(false);
  const [sourceColor, setSourceColor] = useState("#000000");
  const [targetColor, setTargetColor] = useState("#ffffff");
  const [tolerance, setTolerance] = useState(30);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgRemoveTolerance, setBgRemoveTolerance] = useState(40);
  const [showBgOptions, setShowBgOptions] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [cropDragging, setCropDragging] = useState(false);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropMouse, setCropMouse] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (designUrl) loadImageFromUrl(designUrl);
  }, [designUrl]);

  function loadImageFromUrl(url: string) {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
      setImageData(data);
      setOriginalImageData(new ImageData(new Uint8ClampedArray(data.data), data.width, data.height));
      pushHistory(data);
    };
    img.src = url;
  }

  function pushHistory(data: ImageData) {
    const clone = new ImageData(new Uint8ClampedArray(data.data), data.width, data.height);
    setHistory((prev) => {
      const next = prev.slice(0, historyIndex + 1);
      next.push(clone);
      if (next.length > 30) next.shift();
      return next;
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 29));
  }

  function applyImageData(data: ImageData) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.putImageData(data, 0, 0);
    setImageData(new ImageData(new Uint8ClampedArray(data.data), data.width, data.height));
  }

  function undo() {
    if (historyIndex <= 0) return;
    const prev = history[historyIndex - 1];
    if (prev) { setHistoryIndex((i) => i - 1); applyImageData(prev); }
  }

  function redo() {
    if (historyIndex >= history.length - 1) return;
    const next = history[historyIndex + 1];
    if (next) { setHistoryIndex((i) => i + 1); applyImageData(next); }
  }

  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!pickingColor || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    const ctx = canvas.getContext("2d")!;
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    setSourceColor(rgbToHex(pixel[0], pixel[1], pixel[2]));
    setPickingColor(false);
  }

  function applyColorSwap() {
    if (!imageData) return;
    const src = hexToRgb(sourceColor);
    const tgt = hexToRgb(targetColor);
    const newData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    const pixels = newData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      const dist = colorDistance(pixels[i], pixels[i + 1], pixels[i + 2], src[0], src[1], src[2]);
      if (dist <= tolerance) {
        const blend = 1 - dist / tolerance;
        pixels[i] = Math.round(pixels[i] + (tgt[0] - pixels[i]) * blend);
        pixels[i + 1] = Math.round(pixels[i + 1] + (tgt[1] - pixels[i + 1]) * blend);
        pixels[i + 2] = Math.round(pixels[i + 2] + (tgt[2] - pixels[i + 2]) * blend);
      }
    }
    applyImageData(newData);
    pushHistory(newData);
  }

  function invertColors() {
    if (!imageData) return;
    const newData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    const pixels = newData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      pixels[i] = 255 - pixels[i];
      pixels[i + 1] = 255 - pixels[i + 1];
      pixels[i + 2] = 255 - pixels[i + 2];
    }
    applyImageData(newData);
    pushHistory(newData);
  }

  function flipH() {
    if (!imageData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCtx.putImageData(imageData, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save(); ctx.scale(-1, 1);
    ctx.drawImage(tempCanvas, -canvas.width, 0);
    ctx.restore();
    const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setImageData(newData); pushHistory(newData);
  }

  function flipV() {
    if (!imageData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCtx.putImageData(imageData, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save(); ctx.scale(1, -1);
    ctx.drawImage(tempCanvas, 0, -canvas.height);
    ctx.restore();
    const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setImageData(newData); pushHistory(newData);
  }

  function resetToOriginal() {
    if (!originalImageData) return;
    applyImageData(originalImageData);
    pushHistory(originalImageData);
  }

  function handleRemoveColorBg(color?: string) {
    if (!imageData || !canvasRef.current) return;
    const bgColor = color ?? "#ffffff";
    const [bgR, bgG, bgB] = hexToRgb(bgColor);
    const tol = bgRemoveTolerance;
    const newData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    const pixels = newData.data;
    const hardCutoff = tol * 0.8;
    const edgeRange = tol - hardCutoff;
    for (let i = 0; i < pixels.length; i += 4) {
      const dist = colorDistance(pixels[i], pixels[i + 1], pixels[i + 2], bgR, bgG, bgB);
      if (dist <= tol) {
        if (dist <= hardCutoff) {
          pixels[i + 3] = 0;
        } else {
          const t = (dist - hardCutoff) / edgeRange;
          pixels[i + 3] = Math.min(pixels[i + 3], Math.round(t * t * 255));
        }
      }
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.putImageData(newData, 0, 0);
    setImageData(new ImageData(new Uint8ClampedArray(newData.data), newData.width, newData.height));
    pushHistory(newData);
    setShowBgOptions(false);
  }

  async function handleRemoveBgAI() {
    if (!canvasRef.current) return;
    setIsRemovingBg(true);
    setShowBgOptions(false);
    try {
      const { removeBackground } = await import("@imgly/background-removal");
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const blob = await removeBackground(dataUrl, { output: { format: "image/png" } });
      const url = URL.createObjectURL(blob);
      const img = new window.Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setImageData(newData); pushHistory(newData);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    } catch (err) {
      console.error("Background removal failed:", err);
      alert("Arka plan kaldırma başarısız oldu.");
    } finally {
      setIsRemovingBg(false);
    }
  }

  function startCrop() {
    if (!canvasRef.current) return;
    setCropImageUrl(canvasRef.current.toDataURL("image/png"));
    setIsCropping(true);
    setCropRect(null); setCropStart(null); setCropMouse(null);
  }

  function getCropPos(e: React.MouseEvent): { x: number; y: number } | null {
    const container = cropContainerRef.current;
    const img = container?.querySelector("img");
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(e.clientY - rect.top, rect.height)),
    };
  }

  function handleCropMouseDown(e: React.MouseEvent) {
    const pos = getCropPos(e);
    if (!pos) return;
    setCropStart(pos); setCropRect(null); setCropDragging(true);
  }

  function handleCropMouseMove(e: React.MouseEvent) {
    const pos = getCropPos(e);
    if (pos) setCropMouse(pos);
    if (!cropDragging || !cropStart || !pos) return;
    setCropRect({
      x: Math.min(cropStart.x, pos.x), y: Math.min(cropStart.y, pos.y),
      w: Math.abs(pos.x - cropStart.x), h: Math.abs(pos.y - cropStart.y),
    });
  }

  function handleCropMouseUp() { setCropDragging(false); }

  function applyCrop() {
    if (!cropRect || cropRect.w < 5 || cropRect.h < 5 || !imageData) return;
    const container = cropContainerRef.current;
    const img = container?.querySelector("img") as HTMLImageElement | null;
    if (!img) return;
    const displayW = img.clientWidth;
    const displayH = img.clientHeight;
    if (displayW === 0 || displayH === 0) return;
    const scaleX = imageData.width / displayW;
    const scaleY = imageData.height / displayH;
    const sx = Math.round(cropRect.x * scaleX);
    const sy = Math.round(cropRect.y * scaleY);
    const sw = Math.round(cropRect.w * scaleX);
    const sh = Math.round(cropRect.h * scaleY);
    if (sw <= 0 || sh <= 0) return;

    const srcPixels = imageData.data;
    const srcW = imageData.width;
    const newData = new ImageData(sw, sh);
    const dstPixels = newData.data;
    for (let row = 0; row < sh; row++) {
      for (let col = 0; col < sw; col++) {
        const srcIdx = ((sy + row) * srcW + (sx + col)) * 4;
        const dstIdx = (row * sw + col) * 4;
        dstPixels[dstIdx] = srcPixels[srcIdx];
        dstPixels[dstIdx + 1] = srcPixels[srcIdx + 1];
        dstPixels[dstIdx + 2] = srcPixels[srcIdx + 2];
        dstPixels[dstIdx + 3] = srcPixels[srcIdx + 3];
      }
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = sw; canvas.height = sh;
    setImageSize({ w: sw, h: sh });
    const ctx = canvas.getContext("2d")!;
    ctx.putImageData(newData, 0, 0);
    setImageData(new ImageData(new Uint8ClampedArray(newData.data), sw, sh));
    setOriginalImageData(new ImageData(new Uint8ClampedArray(newData.data), sw, sh));
    pushHistory(newData);
    setIsCropping(false); setCropImageUrl(null); setCropRect(null); setCropStart(null); setCropMouse(null);
  }

  function cancelCrop() {
    setIsCropping(false); setCropImageUrl(null); setCropRect(null); setCropStart(null); setCropMouse(null);
  }

  function getDataUrl(): string {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL("image/png");
  }

  const displayScale = imageSize.w > 0 ? Math.min(1, 700 / imageSize.w, 500 / imageSize.h) : 1;

  if (!designUrl) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-[95vw] h-[92vh] max-w-[1400px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Palette className="h-5 w-5 text-indigo-600" />
            Tasarım Editörü
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onSave(getDataUrl())}
              disabled={!imageData}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors"
            >
              <Save className="h-4 w-4" />
              Kaydet
            </button>
            {showMockupActions && onApplyToCanvas && (
              <button
                onClick={() => { onApplyToCanvas(getDataUrl()); onClose(); }}
                disabled={!imageData}
                className="flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
              >
                <ArrowRight className="h-4 w-4" />
                Mockup'a Uygula
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
            >
              <X className="h-4 w-4" />
              Kapat
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Canvas Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0 flex-wrap">
              {/* Bg Remove dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowBgOptions(!showBgOptions)}
                  disabled={isRemovingBg || !imageData}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors disabled:opacity-40 border border-gray-200"
                >
                  {isRemovingBg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eraser className="h-4 w-4" />}
                  {isRemovingBg ? "Kaldırılıyor..." : "Arka Plan Kaldır"}
                  <ChevronDown className="h-3 w-3" />
                </button>
                {showBgOptions && (
                  <div className="absolute top-full left-0 mt-1 z-50 w-72 rounded-xl border border-gray-200 bg-white shadow-xl p-4 space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">Renk Bazlı (Tasarımlar için)</p>
                      <p className="text-[10px] text-gray-400 mb-2">Belirli bir rengi şeffaf yapar. Tasarım detaylarını korur.</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] text-gray-500">Tolerans: {bgRemoveTolerance}</span>
                        <input type="range" min={10} max={120} value={bgRemoveTolerance} onChange={(e) => setBgRemoveTolerance(Number(e.target.value))} className="flex-1 h-1.5 accent-purple-600" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => handleRemoveColorBg("#ffffff")}
                          className="flex items-center justify-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors">
                          <div className="w-4 h-4 rounded border border-gray-300 bg-white" /> Beyaz Kaldır
                        </button>
                        <button onClick={() => handleRemoveColorBg("#000000")}
                          className="flex items-center justify-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors">
                          <div className="w-4 h-4 rounded border border-gray-300 bg-black" /> Siyah Kaldır
                        </button>
                      </div>
                      <button onClick={() => handleRemoveColorBg(sourceColor)}
                        className="w-full mt-2 flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-purple-50 transition-colors">
                        <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: sourceColor }} />
                        Seçili Rengi Kaldır ({sourceColor})
                      </button>
                    </div>
                    <div className="border-t border-gray-100 pt-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">AI ile Kaldır (Fotoğraflar için)</p>
                      <p className="text-[10px] text-gray-400 mb-2">Yapay zeka ile arka planı algılayıp kaldırır.</p>
                      <button onClick={handleRemoveBgAI} disabled={isRemovingBg}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors">
                        {isRemovingBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />}
                        AI Arka Plan Kaldır
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Crop */}
              <button onClick={isCropping ? cancelCrop : startCrop} disabled={!imageData}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${
                  isCropping ? "bg-indigo-50 text-indigo-700 border-indigo-300" : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 border-gray-200"
                } disabled:opacity-40`}>
                <CropIcon className="h-4 w-4" />
                {isCropping ? "Kırpmayı İptal Et" : "Kırp"}
              </button>
              {isCropping && cropRect && cropRect.w > 5 && cropRect.h > 5 && (
                <button onClick={applyCrop}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                  Kırpmayı Uygula
                </button>
              )}

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Undo/Redo */}
              <button onClick={undo} disabled={historyIndex <= 0} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Geri Al">
                <Undo2 className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={redo} disabled={historyIndex >= history.length - 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="İleri Al">
                <Redo2 className="h-4 w-4 text-gray-600" />
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Flip */}
              <button onClick={flipH} disabled={!imageData} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Yatay Çevir">
                <FlipHorizontal className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={flipV} disabled={!imageData} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Dikey Çevir">
                <FlipVertical className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={invertColors} disabled={!imageData} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Renkleri Ters Çevir">
                <span className="text-xs font-bold text-gray-500">INV</span>
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Reset */}
              <button onClick={resetToOriginal} disabled={!originalImageData} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Orijinale Dön">
                <RotateCcw className="h-4 w-4" /> Sıfırla
              </button>
            </div>

            {/* Loading banner */}
            {isRemovingBg && (
              <div className="flex items-center gap-3 px-6 py-3 bg-purple-50 border-b border-purple-200 flex-shrink-0">
                <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                <p className="text-sm text-purple-700 font-medium">Arka plan kaldırılıyor... Bu işlem birkaç dakika sürebilir.</p>
              </div>
            )}

            {/* Canvas Area */}
            <div className="flex-1 flex items-center justify-center overflow-auto p-6">
              {/* Crop overlay */}
              {isCropping && cropImageUrl && (
                <div ref={cropContainerRef} className="relative select-none" style={{ cursor: "crosshair" }}
                  onMouseDown={handleCropMouseDown} onMouseMove={handleCropMouseMove} onMouseUp={handleCropMouseUp} onMouseLeave={handleCropMouseUp}>
                  <img src={cropImageUrl} alt="Crop" draggable={false} style={{ maxWidth: "100%", maxHeight: "calc(92vh - 200px)", display: "block" }} />
                  {cropRect && (
                    <>
                      <div className="absolute inset-0 bg-black/50 pointer-events-none" style={{
                        clipPath: `polygon(0% 0%, 0% 100%, ${cropRect.x}px 100%, ${cropRect.x}px ${cropRect.y}px, ${cropRect.x + cropRect.w}px ${cropRect.y}px, ${cropRect.x + cropRect.w}px ${cropRect.y + cropRect.h}px, ${cropRect.x}px ${cropRect.y + cropRect.h}px, ${cropRect.x}px 100%, 100% 100%, 100% 0%)`,
                      }} />
                      <div className="absolute pointer-events-none border-2 border-white" style={{
                        left: cropRect.x, top: cropRect.y, width: cropRect.w, height: cropRect.h,
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(0,0,0,0.3)",
                      }}>
                        <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/40" />
                        <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/40" />
                        <div className="absolute top-1/3 left-0 right-0 h-px bg-white/40" />
                        <div className="absolute top-2/3 left-0 right-0 h-px bg-white/40" />
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap">
                          {Math.round(cropRect.w)} x {Math.round(cropRect.h)}
                        </div>
                      </div>
                    </>
                  )}
                  {cropMouse && !cropRect && (
                    <>
                      <div className="absolute pointer-events-none" style={{ left: 0, right: 0, top: cropMouse.y, height: 1, background: "rgba(99,102,241,0.6)" }} />
                      <div className="absolute pointer-events-none" style={{ top: 0, bottom: 0, left: cropMouse.x, width: 1, background: "rgba(99,102,241,0.6)" }} />
                    </>
                  )}
                  {cropMouse && cropDragging && (
                    <>
                      <div className="absolute pointer-events-none" style={{ left: 0, right: 0, top: cropMouse.y, height: 1, background: "rgba(99,102,241,0.4)", borderTop: "1px dashed rgba(99,102,241,0.8)" }} />
                      <div className="absolute pointer-events-none" style={{ top: 0, bottom: 0, left: cropMouse.x, width: 1, background: "rgba(99,102,241,0.4)", borderLeft: "1px dashed rgba(99,102,241,0.8)" }} />
                    </>
                  )}
                </div>
              )}
              {/* Canvas */}
              <div className={`flex justify-center items-center rounded-xl p-4 ${pickingColor ? "cursor-crosshair ring-4 ring-indigo-400" : ""}`}
                style={{ background: "repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%) 50% / 20px 20px", display: isCropping ? "none" : undefined }}>
                <canvas ref={canvasRef} onClick={handleCanvasClick}
                  style={{ width: imageSize.w * displayScale, height: imageSize.h * displayScale, imageRendering: "auto", maxWidth: "100%", maxHeight: "calc(92vh - 200px)" }}
                  className="rounded-lg shadow-lg" />
              </div>
            </div>
          </div>

          {/* Right: Sidebar Tools */}
          <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-5 space-y-5">
              {/* Color Swap Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Renk Değiştir</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Kaynak Renk</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={sourceColor} onChange={(e) => setSourceColor(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                      <input type="text" value={sourceColor} onChange={(e) => setSourceColor(e.target.value)} className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono uppercase" />
                      <button onClick={() => setPickingColor(!pickingColor)}
                        className={`p-2.5 rounded-lg border transition-colors ${pickingColor ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "border-gray-200 hover:bg-gray-100"}`}
                        title="Görsel üzerinden renk seç">
                        <Pipette className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Hedef Renk</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={targetColor} onChange={(e) => setTargetColor(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                      <input type="text" value={targetColor} onChange={(e) => setTargetColor(e.target.value)} className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono uppercase" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Tolerans: {tolerance}</label>
                    <input type="range" min={5} max={150} value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} className="w-full h-2 accent-indigo-600 rounded-lg" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => { setSourceColor("#000000"); setTargetColor("#ffffff"); }} disabled={!imageData}
                      className="flex items-center justify-center gap-1 rounded-lg bg-gray-800 px-2 py-2 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-40 transition-colors">
                      <Moon className="h-3 w-3" /><ArrowRight className="h-3 w-3" /><Sun className="h-3 w-3" />
                    </button>
                    <button onClick={() => { setSourceColor("#ffffff"); setTargetColor("#000000"); }} disabled={!imageData}
                      className="flex items-center justify-center gap-1 rounded-lg bg-white border border-gray-200 px-2 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors">
                      <Sun className="h-3 w-3" /><ArrowRight className="h-3 w-3" /><Moon className="h-3 w-3" />
                    </button>
                    <button onClick={applyColorSwap} disabled={!imageData}
                      className="rounded-lg bg-indigo-600 px-2 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors">
                      Uygula
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200" />

              {/* Mockup-specific actions */}
              {showMockupActions && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Mockup'lara Uygula</h3>
                  <div className="space-y-2">
                    {onApplyToCanvas && onClearLight && onClearDark && (
                      <button onClick={() => { onApplyToCanvas(getDataUrl()); onClearLight(); onClearDark(); }} disabled={!imageData}
                        className="w-full flex items-center gap-3 rounded-xl bg-white border-2 border-gray-200 px-3 py-2.5 text-left hover:border-indigo-300 hover:bg-indigo-50/50 disabled:opacity-40 transition-colors">
                        <Save className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-gray-700">Tüm Mockup'lara Uygula</span>
                          <p className="text-[10px] text-gray-400">Tema ayrımı yapmadan tüm mockup'lara ata</p>
                        </div>
                      </button>
                    )}
                    {onSaveLight && (
                      <button onClick={() => onSaveLight(getDataUrl())} disabled={!imageData}
                        className="w-full flex items-center gap-3 rounded-xl bg-white border-2 border-gray-200 px-3 py-2.5 text-left hover:border-amber-300 hover:bg-amber-50/50 disabled:opacity-40 transition-colors">
                        <Sun className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-gray-700">Açık Mockup'lara Ata</span>
                          <p className="text-[10px] text-gray-400">Beyaz/açık renkli mockup'larda kullan</p>
                        </div>
                      </button>
                    )}
                    {onSaveDark && (
                      <button onClick={() => onSaveDark(getDataUrl())} disabled={!imageData}
                        className="w-full flex items-center gap-3 rounded-xl bg-gray-800 border-2 border-gray-600 px-3 py-2.5 text-left hover:border-blue-400 hover:bg-gray-700 disabled:opacity-40 transition-colors">
                        <Moon className="h-4 w-4 text-blue-300 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-gray-200">Koyu Mockup'lara Ata</span>
                          <p className="text-[10px] text-gray-500">Siyah/koyu renkli mockup'larda kullan</p>
                        </div>
                      </button>
                    )}
                  </div>

                  {(lightDesignUrl || darkDesignUrl) && (
                    <div className="mt-3 space-y-2">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Atanan Tema Versiyonları</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="relative rounded-xl bg-white border border-gray-200 p-2 flex flex-col items-center justify-center group" style={{ minHeight: 70 }}>
                          {lightDesignUrl ? (
                            <>
                              <img src={lightDesignUrl} alt="Light" className="max-h-14 max-w-full object-contain rounded" />
                              <span className="text-[9px] text-amber-600 font-medium mt-1 flex items-center gap-0.5"><Sun className="h-2.5 w-2.5" /> Açık</span>
                              <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { if (lightDesignUrl) loadImageFromUrl(lightDesignUrl); }}
                                  className="p-1 rounded bg-white/90 shadow-sm border border-gray-200 hover:bg-indigo-50 text-gray-500 hover:text-indigo-600" title="Editöre yükle">
                                  <Undo2 className="h-3 w-3" />
                                </button>
                                {onClearLight && (
                                  <button onClick={onClearLight}
                                    className="p-1 rounded bg-white/90 shadow-sm border border-gray-200 hover:bg-red-50 text-gray-500 hover:text-red-500" title="Sil">
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-[10px] text-gray-300">Açık yok</span>
                          )}
                        </div>
                        <div className="relative rounded-xl bg-gray-800 border border-gray-600 p-2 flex flex-col items-center justify-center group" style={{ minHeight: 70 }}>
                          {darkDesignUrl ? (
                            <>
                              <img src={darkDesignUrl} alt="Dark" className="max-h-14 max-w-full object-contain rounded" />
                              <span className="text-[9px] text-blue-300 font-medium mt-1 flex items-center gap-0.5"><Moon className="h-2.5 w-2.5" /> Koyu</span>
                              <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { if (darkDesignUrl) loadImageFromUrl(darkDesignUrl); }}
                                  className="p-1 rounded bg-gray-700/90 shadow-sm border border-gray-600 hover:bg-indigo-900 text-gray-400 hover:text-indigo-300" title="Editöre yükle">
                                  <Undo2 className="h-3 w-3" />
                                </button>
                                {onClearDark && (
                                  <button onClick={onClearDark}
                                    className="p-1 rounded bg-gray-700/90 shadow-sm border border-gray-600 hover:bg-red-900 text-gray-400 hover:text-red-400" title="Sil">
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-[10px] text-gray-500">Koyu yok</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
